import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-system-health-card.css?inline';
import '../pk-card/pk-card.js';

/**
 * `<pk-system-health-card>` — status-light card with multiple probe results.
 *
 * Wraps a `<pk-card>` and displays a heading + last-checked timestamp + a
 * stacked list of probe results. Consumers slot in `<pk-status-pill>`
 * (or any element) directly; the card just provides the chrome.
 *
 * Slots:
 *   - default — one or more probe rows (typically `<pk-status-pill>`).
 *
 * Attributes:
 *   - `title`         — card heading (default `System health`).
 *   - `last-checked`  — timestamp display ("12s ago" / "2026-05-15 12:24 UTC").
 */
export class PkSystemHealthCard extends HTMLElement {
    static readonly tagName = 'pk-system-health-card';

    static get observedAttributes(): string[] {
        return ['title', 'last-checked'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private template(): TemplateResult {
        const title       = this.getAttribute('title') ?? 'System health';
        const lastChecked = this.getAttribute('last-checked') ?? '';
        return html`
            <style>${styles}</style>
            <pk-card>
                <div class="header">
                    <h2 class="title">${title}</h2>
                    ${lastChecked ? html`
                        <span class="last-checked" title=${lastChecked}>
                            Last checked ${lastChecked}
                        </span>
                    ` : ''}
                </div>
                <div class="probes">
                    <slot></slot>
                </div>
            </pk-card>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkSystemHealthCard.tagName)) {
    customElements.define(PkSystemHealthCard.tagName, PkSystemHealthCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-system-health-card': PkSystemHealthCard;
    }
}
